import { useMemo, useState } from "react"
import { Box, Input, Text, HStack } from "@chakra-ui/react"
import { type ColumnDef } from "@tanstack/react-table"
import SectionCard from "../../framework/SectionCard"
import DataTable from "../../framework/DataTable"
import ErrorAlert from "../../components/ErrorAlert"
import { extractErrorInfo } from "../../lib/api/error-utils"
import {
  useResortVisitSummary,
  type ResortVisitSummaryRow,
} from "../../hooks/useResortVisitSummary"

const columns: ColumnDef<ResortVisitSummaryRow>[] = [
  {
    id: "index",
    header: "#",
    cell: ({ row, table }) => table.getRowModel().rows.findIndex((r) => r.id === row.id) + 1,
    enableSorting: false,
  },
  {
    accessorKey: "name",
    header: "Resort",
    id: "name",
    enableSorting: true,
  },
  {
    accessorKey: "userCount",
    header: "Users",
    id: "userCount",
    enableSorting: true,
  },
  {
    accessorKey: "liftCount",
    header: "Lifts",
    id: "liftCount",
    enableSorting: true,
  },
]

export default function ResortVisitSummary() {
  const [filter, setFilter] = useState("")
  const [sorting, setSorting] = useState([{ id: "name", desc: false }])
  const { data, isLoading, error } = useResortVisitSummary()

  const rows = useMemo(() => {
    const resorts = data?.resorts ?? []
    const term = filter.trim().toLowerCase()
    if (!term) {
      return resorts
    }

    return resorts.filter((row) => {
      return row.name.toLowerCase().includes(term)
    })
  }, [data, filter])

  const errorInfo = error ? extractErrorInfo(error) : null

  return (
    <SectionCard>
      <Text fontSize="md" mb="3">
        Resort Visit Summary
      </Text>
      <HStack gap="3" mb="4">
        <Input
          placeholder="Filter resorts"
          bg="gray.700"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
      </HStack>

      {errorInfo && <ErrorAlert message={errorInfo.message} traceId={errorInfo.traceId} />}

      <Box overflowX="auto">
        <DataTable
          columns={columns}
          data={rows}
          isLoading={isLoading}
          emptyMessage="No resort visits available."
          sorting={sorting}
          onSortingChange={setSorting}
        />
      </Box>
    </SectionCard>
  )
}
